// ============================================================================
// CHANGO V5.0 — Sensitivity Analysis / Tornado Diagram
// ============================================================================
// Path: /app/products/:code/sensitivity
//
// UI:
//   1. Product header + selector
//   2. Variable selection (multi-checkbox)
//   3. Variation range slider (5-50%)
//   4. "Analyze" button
//   5. Tornado chart (recharts BarChart horizontal)
//
// Access: boss/finance only
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

export default function SensitivityAnalysis() {
  const { code } = useParams();
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';
  const decodedCode = decodeURIComponent(code || '');

  const [product, setProduct] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // User selections
  const [selectedVariables, setSelectedVariables] = useState({
    fx_rate: false,
    quantity: false,
    freight: false,
  });
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const [selectedProcesses, setSelectedProcesses] = useState({});
  const [variationPct, setVariationPct] = useState(10);

  // Analysis result
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Load product data
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const [prodRes, matsRes, procsRes] = await Promise.all([
          api.get(`/api/products/${decodedCode}`),
          api.get('/api/bom-items?archived=false'),
          api.get('/api/processes?archived=false')
        ]);

        setProduct(prodRes);
        setMaterials(matsRes?.items || []);
        setProcesses(procsRes?.items || []);
      } catch (err) {
        setError(err.message || 'Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [decodedCode]);

  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅 boss/finance 可访问敏感性分析
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="px-6 py-12 text-center text-slate-400">加载中…</div>;
  }

  if (!product) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          产品未找到
        </div>
        <div className="mt-4">
          <Link to="/app/products" className="text-sm text-slate-600 hover:underline">
            返回产品列表
          </Link>
        </div>
      </div>
    );
  }

  // Collect selected variables for API call
  const buildVariablesArray = () => {
    const vars = [];
    if (selectedVariables.fx_rate) {
      vars.push({ type: 'fx_rate' });
    }
    if (selectedVariables.quantity) {
      vars.push({ type: 'quantity' });
    }
    if (selectedVariables.freight) {
      vars.push({ type: 'freight' });
    }
    Object.entries(selectedMaterials).forEach(([code, selected]) => {
      if (selected) {
        vars.push({ type: 'material_cost', material_code: code });
      }
    });
    Object.entries(selectedProcesses).forEach(([code, selected]) => {
      if (selected) {
        vars.push({ type: 'process_cost', process_code: code });
      }
    });
    return vars;
  };

  const handleAnalyze = async () => {
    const variables = buildVariablesArray();
    if (variables.length === 0) {
      setError('请至少选择一个变量');
      return;
    }

    setAnalyzing(true);
    setError(null);
    try {
      const response = await api.post('/api/sensitivity/analyze', {
        product_code: decodedCode,
        variables,
        variation_pct: variationPct
      });

      setResult(response);

      // Transform for recharts BarChart (horizontal)
      // Each variable becomes a bar showing low→baseline→high range
      const data = response.variables.map(v => ({
        name: v.label,
        low: v.low,
        baseline: response.baseline.total_profit_rmb,
        high: v.high,
        sensitivity: v.sensitivity
      }));

      setChartData(data);
    } catch (err) {
      setError(err.message || '分析失败');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleVariable = (key) => {
    setSelectedVariables(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleMaterial = (code) => {
    setSelectedMaterials(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  const toggleProcess = (code) => {
    setSelectedProcesses(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  const countSelected = () => {
    const baseCount = Object.values(selectedVariables).filter(Boolean).length;
    const matCount = Object.values(selectedMaterials).filter(Boolean).length;
    const procCount = Object.values(selectedProcesses).filter(Boolean).length;
    return baseCount + matCount + procCount;
  };

  return (
    <div className="px-6 py-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <Link to="/app/products" className="text-xs text-slate-500 hover:text-chango-700">← 返回产品列表</Link>
        <h1 className="mt-1 text-xl font-semibold text-slate-800">敏感性分析</h1>
        <p className="text-sm text-slate-500">
          {product.name} ({product.sku_code}) — 分析变量变化对毛利的影响
        </p>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* ---- Left: Variable Selection ---- */}
        <div className="col-span-3">
          <div className="card p-5 space-y-5 sticky top-6">
            <div>
              <h3 className="font-semibold text-sm text-slate-800 mb-3">选择变量 ({countSelected()})</h3>

              {/* Base variables */}
              <div className="space-y-2 mb-4 pb-4 border-b border-slate-200">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVariables.fx_rate}
                    onChange={() => toggleVariable('fx_rate')}
                    className="w-4 h-4"
                  />
                  <span>汇率</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVariables.quantity}
                    onChange={() => toggleVariable('quantity')}
                    className="w-4 h-4"
                  />
                  <span>数量</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVariables.freight}
                    onChange={() => toggleVariable('freight')}
                    className="w-4 h-4"
                  />
                  <span>运费</span>
                </label>
              </div>

              {/* Material variables */}
              {materials.length > 0 && (
                <div className="mb-4 pb-4 border-b border-slate-200">
                  <p className="text-xs font-semibold text-slate-600 mb-2 uppercase">物料成本</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {materials.map(mat => (
                      <label key={mat.code} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMaterials[mat.code] || false}
                          onChange={() => toggleMaterial(mat.code)}
                          className="w-3 h-3"
                        />
                        <span className="truncate">{mat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Process variables */}
              {processes.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-600 mb-2 uppercase">工序成本</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {processes.map(proc => (
                      <label key={proc.code} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedProcesses[proc.code] || false}
                          onChange={() => toggleProcess(proc.code)}
                          className="w-3 h-3"
                        />
                        <span className="truncate">{proc.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Variation % */}
            <div className="border-t border-slate-200 pt-4">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                变化范围: {variationPct}%
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={variationPct}
                onChange={(e) => setVariationPct(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex gap-1 text-xs text-slate-500 mt-1">
                <span>-{variationPct}%</span>
                <span className="flex-1"></span>
                <span>+{variationPct}%</span>
              </div>
            </div>

            {/* Analyze Button */}
            <button
              onClick={handleAnalyze}
              disabled={analyzing || countSelected() === 0}
              className="w-full btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzing ? '分析中…' : '分析'}
            </button>
          </div>
        </div>

        {/* ---- Right: Tornado Chart & Results ---- */}
        <div className="col-span-9">
          {result && chartData.length > 0 ? (
            <div className="card p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-xs text-slate-600 mb-1">基线毛利</p>
                  <p className="text-xl font-bold text-slate-800">
                    ¥{result.baseline.total_profit_rmb.toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-3">
                  <p className="text-xs text-slate-600 mb-1">基线收入</p>
                  <p className="text-xl font-bold text-slate-800">
                    ¥{result.baseline.total_revenue_rmb.toFixed(2)}
                  </p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded p-3">
                  <p className="text-xs text-slate-600 mb-1">基线成本</p>
                  <p className="text-xl font-bold text-slate-800">
                    ¥{result.baseline.total_cost_rmb.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Tornado Chart */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-4">龙卷风图（按敏感度排序）</h3>
                <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                  >
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => `¥${value.toFixed(2)}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    />
                    <ReferenceLine
                      x={result.baseline.total_profit_rmb}
                      stroke="#666"
                      strokeDasharray="5 5"
                      label={{ value: '基线', position: 'top', fontSize: 11, fill: '#666' }}
                    />
                    <Bar dataKey="low" fill="#ef4444" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="high" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Sensitivity Details */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">敏感度排行</h3>
                <div className="space-y-2 text-sm">
                  {result.variables.map((v, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{v.label}</p>
                        <p className="text-xs text-slate-500">
                          下限: ¥{v.low.toFixed(2)} — 上限: ¥{v.high.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-800">{(v.sensitivity * 100).toFixed(1)}%</p>
                        <p className="text-xs text-slate-500">敏感度</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-12 text-center text-slate-400">
              <p className="mb-2">选择变量并点击"分析"按钮查看龙卷风图</p>
              <p className="text-xs">基于选定的变量范围（±{variationPct}%）计算毛利变化</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
